import { useCallback, useMemo, useState } from 'react'
import { FileSearch } from 'lucide-react'
import {
  DataTable,
  type DataTableColumn,
  type DataTableBulkAction,
  type DataTableSortState,
  StatusBadge,
  EmptyState,
  toast,
} from '@atrvd/saas-ui'
import { cn } from '@atrvd/saas-ui'
import { formatCategoryAmount, formatDate } from '@dcp/shared/utils/formatters'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { queryErrorDisplay, type TableQueryStateProps } from '@/components/common/queryErrorDisplay'
import { buildDataTablePagination, type PaginationState } from '@/lib/pagination'
import type {
  ApiSyncStatus,
  AssignableCategory,
  BankTransaction,
  BankTransactionSort,
} from '@/types/banking'
import { isBankTransactionSortKey } from '@/types/banking'
import { TransactionCategorySelect } from './TransactionCategorySelect'

/**
 * `refreshing`: the rows are the previous page/filter's, standing in while the
 * next one loads. They stay visible, dimmed (no skeleton), but cannot be
 * ticked: a row ticked now vanishes when the page lands yet would stay in the
 * selection, and the bulk action would write a row the user no longer sees.
 */
interface BankTransactionsTableProps extends TableQueryStateProps {
  transactions: BankTransaction[]
  onClearFilters?: () => void
  /** Page, size and total of the server-paged result; the owner keeps page and size in the URL. */
  pagination: PaginationState
  /** Categories offered by the inline Category dropdown and the bulk action (DCP-123). */
  categories: AssignableCategory[]
  /**
   * Assign `category` to the transactions `ids`. Resolves with the number of
   * rows actually written, rejects on failure; the mutation behind it toasts
   * the error and updates the cache optimistically, so the table only reacts
   * to success.
   */
  onAssignCategory: (ids: string[], category: AssignableCategory) => Promise<number>
  /** Active server-side sort; absent = the default order (newest first). */
  sort?: BankTransactionSort
  /** Report a header click; `undefined` = back to the default order. */
  onSortChange: (sort: BankTransactionSort | undefined) => void
  /**
   * Row selection is cleared whenever this changes. The page derives it from
   * the result set's scope (filters / organization), so a selection never
   * silently carries ids the user can no longer see across a filter change.
   */
  selectionResetKey: string
  /**
   * Whether the viewer may change categories (`manage_banking`, DCP-123). When
   * false the table is read-only as before this ticket: the Category cell is
   * plain text and there is no row selection and no bulk bar. `treasury` and
   * `view_only` can open Daily Banking (`view_banking`) but must not edit it.
   */
  canCategorize: boolean
}

/**
 * Map the DataTable's sort state onto ours. The table runs single-column sort,
 * so only the first entry matters; an empty array — or a null direction — is
 * the third click of the library's asc → desc → none cycle, i.e. "back to the
 * default order".
 *
 * @param state - Sort state emitted by the DataTable.
 * @returns The equivalent {@link BankTransactionSort}, or `undefined` for none.
 */
function toBankTransactionSort(state: DataTableSortState[]): BankTransactionSort | undefined {
  const [first] = state
  if (!first || !first.direction || !isBankTransactionSortKey(first.columnKey)) return undefined
  return { key: first.columnKey, direction: first.direction }
}

/**
 * Row class for the DCP-123 "needs a category" highlight (AC#1), derived from
 * the row's CURRENT value so it disappears as soon as a category lands.
 *
 * Prototype: #fffbeb on the whole row plus an inset 3px #f59e0b stripe on the
 * left edge. Notes on how it composes with the saas-ui row classes (merged by
 * tailwind-merge in DataTableBody, row class LAST):
 *   - the amber `bg-*` replaces the row's `hover:bg-muted/50` and selected
 *     `bg-muted/50`, so hover and selected get amber shades of their own —
 *     otherwise hovering would grey the row out and a selected uncategorized
 *     row would not read as selected;
 *   - the stripe sits on the FIRST CELL (the selection checkbox cell), not on
 *     the <tr>: box-shadow on a table row is not reliably painted by browsers.
 *
 * @param uncategorized - Whether the row has no category.
 * @param selected - Whether the row is currently selected.
 * @returns Classes to append to the row, or `undefined`.
 */
function categoryHighlightClass(uncategorized: boolean, selected: boolean): string | undefined {
  if (!uncategorized) return undefined
  const stripe = '[&>td:first-child]:shadow-[inset_3px_0_0_theme(colors.amber.500)]'
  return selected
    ? `${stripe} bg-amber-100 hover:bg-amber-100 dark:bg-amber-900/40 dark:hover:bg-amber-900/40`
    : `${stripe} bg-amber-50 hover:bg-amber-100/60 dark:bg-amber-950/30 dark:hover:bg-amber-950/50`
}

/**
 * Render the "API Status" cell.
 *
 * Bank-feed rows (LedgerSync, and the historic Plaid ones) show their ingest
 * status as a badge; rows the API never touched — manual entries and file
 * imports — get a muted "-", mirroring how {@link BalanceImportTable}'s own
 * "API Update Status" column renders accounts with no upstream API (DCP-344).
 *
 * @param status - Ingest status, or `null` when the row has no API origin.
 * @returns A StatusBadge for API rows, a muted dash otherwise.
 */
function getApiStatusBadge(status: ApiSyncStatus | null) {
  if (status === null) {
    return <span className="text-muted-foreground text-sm">-</span>
  }

  const statusMap: Record<string, { status: 'success' | 'pending' | 'error'; label: string }> = {
    synced: { status: 'success', label: 'Synced' },
    pending: { status: 'pending', label: 'Pending' },
    error: { status: 'error', label: 'Error' },
  }

  const { status: statusType, label } = statusMap[status] || {
    status: 'pending' as const,
    label: status,
  }

  return <StatusBadge status={statusType} label={label} showIcon />
}

/**
 * Column definitions for the bank transactions table.
 * Each column defines how data is displayed and formatted.
 *
 * A factory rather than a module constant since DCP-123: the Category cell is
 * now an inline dropdown that needs the assignable categories and the save
 * callback. The KEYS stay exactly as they were — column prefs are persisted by
 * key (see the `sageStatus` note below) and `useTableColumnPrefs` reconciles on
 * key content, not on the array identity, so rebuilding the array is harmless.
 *
 * Every column except MEMO is `sortable` (DCP-123, AC#6); the sort itself runs
 * server-side, see the `sorting` prop of the DataTable below.
 *
 * @param categories - Options of the inline Category dropdown.
 * @param onAssignRow - Persists a category for ONE row (the inline dropdown),
 *   or `null` for a viewer without `manage_banking` — the cell is then plain text.
 * @returns The table's column definitions.
 */
function buildColumns(
  categories: AssignableCategory[],
  onAssignRow: ((id: string, category: AssignableCategory) => Promise<void>) | null
): DataTableColumn<BankTransaction>[] {
  return [
    {
      key: 'facility',
      header: 'Facility',
      cellClassName: 'font-medium',
      sortable: true,
    },
    {
      key: 'date',
      header: 'Date',
      // The Pending badge rides WITH the date rather than living in a column of
      // its own, for three reasons (DCP-355):
      //   1. "API Status" already renders a badge labelled "Pending", meaning our
      //      ingest has not reported yet. Two identically-labelled badges in one
      //      row, meaning different things, is worse than no badge at all.
      //   2. Column prefs are stored per user by column KEY, and `reconcile`
      //      treats an unknown key as a NEW column — which defaults to HIDDEN
      //      (see lib/columnPrefs.ts and the `sageStatus` note below). A new
      //      column would be invisible to everyone who has ever opened Columns.
      //   3. The date is precisely what is provisional: a pending charge has no
      //      posted date yet, and the bank may still change its amount and
      //      description before it settles.
      render: (txn) => (
        <span className="inline-flex items-center gap-2">
          {formatDate(txn.date)}
          {txn.pending && (
            <>
              {/*
               * Reason 1 above solves the collision only for people who can SEE
               * which cell a badge sits in. A screen reader reads the row as a
               * sequence, so two badges both announcing "Pending" are
               * indistinguishable by position. The visible badge is therefore
               * hidden from assistive tech and the meaning is carried as real
               * text — the same treatment used in SettingsFacilitiesPage
               * (PR #208 review #3).
               */}
              <span className="sr-only">Not yet posted by the bank</span>
              <span aria-hidden="true">
                <StatusBadge status="pending" label="Pending" />
              </span>
            </>
          )}
        </span>
      ),
      // Mandatory anchor column — always visible, cannot be hidden via column config.
      disableHide: true,
      sortable: true,
    },
    {
      key: 'accountNumber',
      header: 'Acct number',
      sortable: true,
    },
    {
      key: 'accountName',
      header: 'Account name',
      sortable: true,
    },
    {
      key: 'category',
      header: 'Category',
      // DCP-123 (AC#2/#3): an always-visible dropdown that saves on pick. The
      // current category's name is still shown verbatim (categories.name) — the
      // dropdown's label for the current value comes from the assignable list,
      // or from `txn.category` when that category is not in the list — never via
      // getCategoryLabel, which title-cases Plaid UPPER_SNAKE keys (DCP-223).
      render: (txn) =>
        onAssignRow ? (
          <TransactionCategorySelect
            transaction={txn}
            categories={categories}
            onAssign={(category) => onAssignRow(txn.id, category)}
          />
        ) : (
          txn.category
        ),
      sortable: true,
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (txn) => {
        // Sign + colour driven by category type (DCP-124), shared with
        // ManualTransactionsTable via formatCategoryAmount. Amounts render with
        // cents (DCP-392) — Touch Support needs exact figures to post to Sage
        // Intacct; uncategorized rows fall back to the stored amount sign.
        const amt = formatCategoryAmount(txn.amount, txn.categoryType)
        return <span className={cn('font-medium', amt.className)}>{amt.text}</span>
      },
      sortable: true,
    },
    {
      key: 'description',
      header: 'Description',
      /**
       * DCP-361: bank descriptions are fixed-width-field strings up to ~120 chars
       * and must be shown in full, wrapped over several lines — never clipped to a
       * single line with an ellipsis.
       *
       * The table is `table-layout: auto` (the saas-ui Table has no `table-fixed`),
       * so only two of the three sizing knobs do anything here:
       *   - `minWidth` lands on the <th> inline style (saas-ui data-table
       *     component.tsx) and the auto algorithm honours it as a hard column
       *     floor — this is what makes the column wider.
       *   - `max-w-[420px]` on this BLOCK element caps the cell's max-content
       *     contribution, which is what forces the wrap on a wide monitor. The cap
       *     must live on the inner element: a column-level `maxWidth` would land on
       *     the <th>, and max-width on a table cell is ignored in auto layout (the
       *     same trap documented in CashPositionSummaryGrid, DCP-293).
       * `break-words` is a safety net for a description with no spaces at all.
       * Whitespace stays at the HTML default (runs collapse): the padding spaces of
       * the bank feed are field alignment, not content, and the DOM text node —
       * hence copy/paste and the CSV export — still carries the exact string.
       */
      minWidth: '320px',
      render: (txn) => (
        <div className="max-w-[420px] whitespace-normal break-words">{txn.description}</div>
      ),
      sortable: true,
    },
    {
      key: 'memo',
      header: 'MEMO',
      render: (txn) => txn.memo || '',
    },
    {
      // The key stays `sageStatus` on purpose even though the label and the model
      // field are now API-flavoured (DCP-344): column show/hide + order prefs are
      // persisted per user by column KEY (see lib/columnPrefs.ts), and `reconcile`
      // treats an unknown key as a NEW column, which defaults to HIDDEN. Renaming
      // it would make this column silently disappear for everyone who has ever
      // touched the Columns control. Labels are not part of the stored blob.
      key: 'sageStatus',
      header: 'API Status',
      render: (txn) => getApiStatusBadge(txn.apiStatus),
      sortable: true,
    },
  ]
}

/**
 * Bank transactions table component
 * Displays transactions with filtering, pagination, and status indicators
 * Uses DataTable component from saas-ui for consistent table rendering
 *
 * DCP-123 (Transactions Categorization) adds, all on saas-ui building blocks:
 *   - the amber highlight of uncategorized rows (`rowClassName`);
 *   - the inline Category dropdown ({@link TransactionCategorySelect});
 *   - row selection that survives paging + an inline bulk bar with a
 *     "Category" select and an Apply button (DataTable `selection` +
 *     `bulkActions` with a `select` action), drawn between the card header and
 *     the table as in the prototype and on Projections Input;
 *   - server-side sorting on every column but MEMO (DataTable `sorting`).
 */
export function BankTransactionsTable({
  transactions,
  loading,
  refreshing,
  error,
  retrying,
  onRetry,
  onClearFilters,
  pagination,
  categories,
  onAssignCategory,
  sort,
  onSortChange,
  selectionResetKey,
  canCategorize,
}: BankTransactionsTableProps) {
  /**
   * Selected transaction ids — ALL pages, not just the visible one (DCP-123,
   * AC#4). Owned here rather than by the DataTable because the library only
   * holds the keys it is given: its header checkbox toggles the CURRENT page's
   * rows and leaves the rest of the set alone, which is exactly the prototype's
   * "select page, keep the others" behaviour.
   */
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(() => new Set())

  /**
   * Save the inline dropdown's pick for one row, and drop that row from the
   * selection (DCP-123). Once the user has categorized a row by hand, a later
   * bulk Apply must not silently overwrite that choice — and under an active
   * Category filter the row even leaves the visible result set after the
   * refetch, so it would be written without the user seeing it. Deselected only
   * once the save SUCCEEDS: a failed save is rolled back to the old category,
   * so the row is exactly what the user selected and must stay in the
   * selection. The rejection propagates to the caller (the dropdown handles it).
   *
   * @param id - The row being categorized.
   * @param category - The category picked in its dropdown.
   */
  const assignRow = useCallback(
    async (id: string, category: AssignableCategory) => {
      await onAssignCategory([id], category)
      setSelectedIds((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
    [onAssignCategory]
  )

  const columns = useMemo(
    () => buildColumns(categories, canCategorize ? assignRow : null),
    [categories, canCategorize, assignRow]
  )
  // Persisted column show/hide + order preferences, scoped to this table.
  const columnPrefs = useTableColumnPrefs('bank-transactions', columns)

  /**
   * Clear the selection whenever the page says the result set's scope changed.
   * Adjusted during render (the React-sanctioned "derive from props" form, the
   * same one SettingsCategoriesPage and ConnectionDetailHeader use) rather than
   * in an effect, so no frame ever shows the old selection — and its bar —
   * against the new rows. The last-seen key is STATE, not a ref, so the
   * StrictMode double render compares against the same committed value.
   */
  const [selectionScope, setSelectionScope] = useState(selectionResetKey)
  if (selectionScope !== selectionResetKey) {
    setSelectionScope(selectionResetKey)
    setSelectedIds(new Set())
  }

  /**
   * Apply the picked category to EVERY selected row (DCP-123, AC#4). The ids
   * come from our own `selectedIds`, so rows selected on other pages are
   * included — NOT from the bar's `selectedRows` argument, which the DataTable
   * resolves against the rows it was given, i.e. the current page only.
   *
   * On success the SUBMITTED ids leave the selection, as in the prototype
   * (clearing it unmounts the bar and so resets its select); a row ticked while
   * the request was in flight stays selected. The toast reports the rows the
   * server actually wrote, which can be fewer than submitted when a sync
   * removed some in the meantime.
   *
   * On failure the rejection is deliberately NOT caught: the mutation hook has
   * already toasted the error, and letting it reach the DataTable's bulk bar
   * is what tells the bar "not applied" — the bar then consumes it (no
   * unhandled rejection) and KEEPS the picked category, while the selection
   * stays untouched (it is only cleared on success), so a retry is a
   * single click on Apply.
   *
   * @param categoryId - Id of the category picked in the bar's select.
   */
  const handleBulkAssign = useCallback(
    async (categoryId: string) => {
      const category = categories.find((c) => c.id === categoryId)
      if (!category) return
      const ids = Array.from(selectedIds, String)
      // A rejection propagates to the bar on purpose, see above
      const written = await onAssignCategory(ids, category)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.delete(id)
        return next
      })
      toast.success(
        `"${category.name}" applied to ${written} ${written === 1 ? 'transaction' : 'transactions'}`
      )
    },
    [categories, selectedIds, onAssignCategory]
  )

  /**
   * The bar's single action, per the prototype: "Category", a select, then
   * Apply (disabled until a category is picked). The bar itself shows the
   * "N rows selected" count of the WHOLE selection (every page included, since
   * the DataTable counts the keys we hand it) and the Clear selection button.
   */
  const bulkActions = useMemo<DataTableBulkAction<BankTransaction>[]>(
    () => [
      {
        type: 'select',
        id: 'assign-category',
        label: 'Category',
        placeholder: 'Select category…',
        options: categories.map((c) => ({ value: c.id, label: c.name })),
        applyLabel: 'Apply',
        onApply: handleBulkAssign,
      },
    ],
    [categories, handleBulkAssign]
  )

  // Hidden at ≤ 20 rows (lib/pagination.ts).
  const paginationConfig = buildDataTablePagination(pagination)

  const { banner, tableError } = queryErrorDisplay({ error, retrying, onRetry }, transactions.length)

  return (
    <>
      {banner}
      <DataTable
        title="Bank Transactions"
        className="min-h-[680px]"
        columns={columns}
        data={transactions}
        loading={loading}
        refreshing={refreshing}
        error={tableError}
        density="compact"
        pagination={paginationConfig}
        rowKey={(txn) => txn.id}
        // DCP-123 (AC#6): the Daily Banking query sorts server-side over ALL
        // pages, so the table only reports header clicks. The library cycles
        // asc → desc → none; "none" returns to the default order (newest first).
        sorting={{
          mode: 'server',
          sortState: sort ? [{ columnKey: sort.key, direction: sort.direction }] : [],
          onSortChange: (state) => onSortChange(toBankTransactionSort(state)),
        }}
        // Selection + bulk bar only for viewers who may categorize (manage_banking).
        selection={
          canCategorize
            ? {
                // `mode` is required in practice: `useSelection` defaults it to
                // 'none' and then ignores every click (see ConnectionAccountsTable).
                mode: 'multiple',
                selectedRows: selectedIds,
                onSelectionChange: setSelectedIds,
                // Placeholder rows must not be selectable: they may be replaced
                // when the page lands, and the bulk Category apply would write
                // a row that is no longer shown.
                isRowSelectable: () => !refreshing,
              }
            : undefined
        }
        bulkActions={canCategorize ? bulkActions : undefined}
        // No empty strip above the rows while nothing is selected.
        collapseEmptyBulkActions
        // DCP-361: `align-top` is needed because the Description cell now wraps
        // over up to three lines — under the default `align-middle` the
        // single-line cells (Date, Amount, Facility) would float in the vertical
        // centre of a tall row. The compiled `[&>td]` selector outranks a plain
        // `align-middle` coming from a column's `cellClassName` (specificity
        // 0,1,1 vs 0,1,0, and they sit on different elements so tailwind-merge
        // cannot reconcile them), so this applies to EVERY cell in the row.
        rowClassName={(txn) =>
          cn(
            '[&>td]:align-top',
            categoryHighlightClass(txn.categoryId === null, selectedIds.has(txn.id))
          )
        }
        enableColumnReorder
        {...columnPrefs}
        emptyState={
          // DCP-173, AC.5: "If no transactions are available from the bank API,
          // indicate that no transaction data was found." The title matches that
          // acceptance-criterion wording; the description covers BOTH causes
          // (active filters vs. a genuine absence of data) because this table does
          // not distinguish the two — the Clear Filters action is offered whenever
          // the parent supplies a filter reset.
          <EmptyState
            icon={FileSearch}
            title="No transaction data found"
            description="Adjust filters or import transactions to view data"
            action={
              onClearFilters ? { label: 'Clear Filters', onClick: onClearFilters } : undefined
            }
          />
        }
        itemName="transactions"
      />
    </>
  )
}
